// Minimal .ics (iCalendar) parser — pulls VEVENTs out of a file exported from
// Google Calendar, Outlook, Apple Calendar, etc. Recurring events (RRULE) come
// in as their first occurrence only.

// Lines longer than 75 chars are "folded": continued on the next line, which
// starts with a space or tab.
function unfoldLines(text) {
  return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n[ \t]/g, '').split('\n');
}

function unescapeText(value) {
  return value
    .replace(/\\n/gi, '\n')
    .replace(/\\,/g, ',')
    .replace(/\\;/g, ';')
    .replace(/\\\\/g, '\\');
}

// "DTSTART;TZID=America/Chicago:20261014T090000" → { name, params, value }
function parseLine(line) {
  const colon = line.indexOf(':');
  if (colon === -1) return null;
  const [name, ...paramParts] = line.slice(0, colon).split(';');
  const params = {};
  paramParts.forEach(p => {
    const [k, v] = p.split('=');
    if (k && v) params[k.toUpperCase()] = v.replace(/^"|"$/g, '');
  });
  return { name: name.toUpperCase(), params, value: line.slice(colon + 1) };
}

// Milliseconds that `timeZone` is ahead of UTC at instant `ts`.
function tzOffset(ts, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(ts));
  const get = type => Number(parts.find(p => p.type === type).value);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return asUtc - ts;
}

// Returns { date: Date, allDay: boolean } or null.
function parseDate({ params, value }) {
  const m = value.match(/^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})?(Z)?)?$/);
  if (!m) return null;
  const [, y, mo, d, h, mi, s = '0', z] = m;

  if (h === undefined || params.VALUE === 'DATE') {
    return { date: new Date(+y, +mo - 1, +d), allDay: true };
  }
  if (z) {
    return { date: new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi, +s)), allDay: false };
  }
  if (params.TZID) {
    try {
      const wallClock = Date.UTC(+y, +mo - 1, +d, +h, +mi, +s);
      let ts = wallClock - tzOffset(wallClock, params.TZID);
      ts = wallClock - tzOffset(ts, params.TZID); // second pass settles DST edges
      return { date: new Date(ts), allDay: false };
    } catch {
      // Unknown zone name (Outlook sometimes writes Windows names) — fall
      // through and treat it as local time.
    }
  }
  return { date: new Date(+y, +mo - 1, +d, +h, +mi, +s), allDay: false };
}

export function parseIcs(text) {
  const events = [];
  let current = null;
  let depth = 0; // skip nested blocks like VALARM inside a VEVENT

  for (const line of unfoldLines(text)) {
    if (line === 'BEGIN:VEVENT') { current = {}; depth = 0; continue; }
    if (!current) continue;
    if (line.startsWith('BEGIN:')) { depth++; continue; }
    if (line.startsWith('END:') && depth > 0) { depth--; continue; }
    if (line === 'END:VEVENT') {
      if (current.start && current.status !== 'CANCELLED') {
        const { start, end } = current;
        events.push({
          uid: current.uid || '',
          title: current.title || '(No title)',
          start: start.date,
          // All-day DTEND is exclusive; a missing DTEND means a single day/instant.
          end: end ? end.date : (start.allDay ? new Date(start.date.getFullYear(), start.date.getMonth(), start.date.getDate() + 1) : start.date),
          allDay: start.allDay,
          location: current.location || '',
          description: current.description || '',
        });
      }
      current = null;
      continue;
    }
    if (depth > 0) continue;

    const prop = parseLine(line);
    if (!prop) continue;
    switch (prop.name) {
      case 'UID':         current.uid = prop.value; break;
      case 'SUMMARY':     current.title = unescapeText(prop.value).trim(); break;
      case 'LOCATION':    current.location = unescapeText(prop.value).trim(); break;
      case 'DESCRIPTION': current.description = unescapeText(prop.value).trim(); break;
      case 'STATUS':      current.status = prop.value.toUpperCase(); break;
      case 'DTSTART':     current.start = parseDate(prop); break;
      case 'DTEND':       current.end = parseDate(prop); break;
      default: break;
    }
  }
  return events;
}
